import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  MdSchool,
  MdSend,
  MdDomain,
  MdCheckCircle,
  MdSecurity,
  MdBuild,
  MdStar,
  MdVerifiedUser,
  MdCleaningServices,
  MdCalendarToday,
} from "react-icons/md";
import { useSectorPersonnel } from "context/SectorPersonnelContext";
import { useTraining } from "context/TrainingContext";
import { getSectors } from "api/sectors";
import { getTrainingTopicsBySector } from "api/trainingTopics";

const SECONDARY = "#f27405";

const TOPIC_ICONS_MAP = {
  "text-orange-500": MdSecurity,
  "text-blue-500": MdBuild,
  "text-emerald-500": MdVerifiedUser,
  "text-purple-500": MdStar,
  "text-amber-500": MdCleaningServices,
  "text-red-500": MdSecurity,
  "text-cyan-500": MdVerifiedUser,
};

const CURRENT_SUPERVISOR_ID = "w-1";

/* ── Single topic card with its own status, notes, and submit ── */
function TopicCard({ topic, sectorId, sectorName, supervisorId, onSubmitted }) {
  const { addTraining } = useTraining();
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const iconColor = topic.iconColor || "text-slate-500";
  const Icon = TOPIC_ICONS_MAP[iconColor] || MdSchool;
  const canSubmit = status === "completed" || status === "not_completed";

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload = {
      id: `tr-${Date.now()}-${topic.id}`,
      sectorId,
      sectorName,
      supervisorId,
      topicId: topic.id,
      topicName: topic.name,
      topicStatuses: { [topic.id]: status },
      topicNotes: { [topic.id]: notes },
      date: new Date().toISOString(),
    };
    addTraining(payload);
    setSubmitted(true);
    if (onSubmitted) onSubmitted(topic.id);
  };

  const formattedDate = topic.startDate
    ? new Date(topic.startDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  if (submitted) {
    return (
      <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <span className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 shrink-0">
            <MdCheckCircle className="text-xl text-emerald-600 dark:text-emerald-400" />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-emerald-800 dark:text-emerald-200 text-sm">{topic.name}</p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
              Submitted as <strong>{status === "completed" ? "Completed" : "Not completed"}</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      {/* Topic header */}
      <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-start gap-3">
          <span className={`p-2 rounded-xl shrink-0 ${iconColor} bg-slate-100 dark:bg-slate-800`}>
            <Icon className="text-lg" />
          </span>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-slate-900 dark:text-white text-sm">{topic.name}</h4>
            {topic.definition && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{topic.definition}</p>
            )}
            {formattedDate && (
              <div className="flex items-center gap-1.5 mt-2">
                <MdCalendarToday className="text-xs text-slate-400 shrink-0" />
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                  Start date: {formattedDate}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status + notes + submit */}
      <div className="p-4 sm:p-5 space-y-3">
        {/* Status toggle */}
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full max-w-sm gap-1">
          {["completed", "not_completed"].map((s) => (
            <React.Fragment key={s}>
              <input
                type="radio"
                name={`status-${topic.id}`}
                id={`${topic.id}-${s}`}
                checked={status === s}
                onChange={() => setStatus(s)}
                className="sr-only"
              />
              <label
                htmlFor={`${topic.id}-${s}`}
                className={`flex-1 text-center py-2.5 text-sm font-bold rounded-lg cursor-pointer transition-all ${
                  status === s
                    ? "text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
                style={status === s ? { backgroundColor: s === "completed" ? "#059669" : SECONDARY } : {}}
              >
                {s === "completed" ? "Completed" : "Not completed"}
              </label>
            </React.Fragment>
          ))}
        </div>

        {/* Notes */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={`Notes for ${topic.name}…`}
          rows={2}
          className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400 resize-none min-h-[56px] transition-shadow"
        />

        {/* Submit button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-5 py-2.5 rounded-xl text-white font-bold text-sm shadow-lg hover:opacity-95 active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: SECONDARY }}
          >
            <MdSend className="text-base" />
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════════ */
export default function Training() {
  const { sectorPersonnel } = useSectorPersonnel();
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [sectorsError, setSectorsError] = useState(null);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState(null);
  const [selectedSectorId, setSelectedSectorId] = useState("");
  const [submittedCount, setSubmittedCount] = useState(0);

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    setSectorsError(null);
    try {
      const result = await getSectors();
      const list = (result.data || []).map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        icon_key: s.icon_key,
      }));
      setSectors(list);
    } catch (err) {
      setSectorsError(err.response?.data?.message || err.message || "Failed to load sectors");
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  const selectedSector = sectors.find((s) => s.id === selectedSectorId);

  const fetchTopicsForSector = useCallback(async (sectorName) => {
    if (!sectorName?.trim()) {
      setTrainingTopics([]);
      return;
    }
    setTopicsLoading(true);
    setTopicsError(null);
    try {
      const result = await getTrainingTopicsBySector(sectorName);
      const list = (result.data || []).map((t) => ({
        id: t.id,
        name: t.name,
        definition: t.definition || "",
        sector_name: t.sector_name,
        startDate: t.start_date,
        iconColor: t.iconColor,
      }));
      setTrainingTopics(list);
    } catch (err) {
      setTopicsError(err.response?.data?.message || err.message || "Failed to load training topics");
      setTrainingTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSector?.name) {
      fetchTopicsForSector(selectedSector.name);
    } else {
      setTrainingTopics([]);
      setTopicsError(null);
    }
  }, [selectedSector?.name, fetchTopicsForSector]);

  const mySectors = useMemo(() => {
    return sectors.filter((s) => {
      const personnel = sectorPersonnel[s.id];
      return personnel?.supervisorIds?.includes(CURRENT_SUPERVISOR_ID);
    });
  }, [sectors, sectorPersonnel]);

  // Fallback: if no sectors match personnel filter, show all sectors
  const sectorsToShow = mySectors.length > 0 ? mySectors : sectors;

  const handleSectorChange = (e) => {
    const id = e.target.value;
    setSelectedSectorId(id);
    setSubmittedCount(0);
  };

  const handleTopicSubmitted = () => {
    setSubmittedCount((prev) => prev + 1);
  };

  useEffect(() => {
    if (sectorsToShow.length === 1 && selectedSectorId !== sectorsToShow[0].id) {
      setSelectedSectorId(sectorsToShow[0].id);
    }
  }, [sectorsToShow, selectedSectorId]);

  return (
    <div className="p-4 md:p-6 pt-4 md:pt-6 space-y-6 max-w-[1440px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-start gap-4">
        <div className="space-y-1">
          <span
            className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
            style={{ backgroundColor: `${SECONDARY}20`, color: SECONDARY }}
          >
            Supervisor
          </span>
          <h2 className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black tracking-tight">
            Operator training
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-2xl">
            Select a sector, then evaluate each training topic individually. You can submit one at a time or multiple.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="w-full lg:w-80 flex flex-col gap-6 shrink-0">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
              <span className="p-2 rounded-xl bg-[#0e76a8]/10 text-[#0e76a8]">
                <MdDomain className="text-lg" />
              </span>
              <h3 className="font-black text-slate-900 dark:text-white text-base">Sector</h3>
            </div>
            <div className="p-4 sm:p-5">
              {sectorsLoading ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading sectors…</p>
              ) : sectorsError ? (
                <p className="text-sm text-red-500">{sectorsError}</p>
              ) : sectorsToShow.length === 0 ? (
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                  Not assigned to any sector. Ask an admin in Sector Management.
                </p>
              ) : (
                <select
                  value={selectedSectorId}
                  onChange={handleSectorChange}
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none font-medium transition-shadow"
                >
                  <option value="">Select sector…</option>
                  {sectorsToShow.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Summary when topics have been submitted */}
          {submittedCount > 0 && (
            <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 shrink-0">
                <MdCheckCircle className="text-xl text-emerald-600 dark:text-emerald-400" />
              </span>
              <div>
                <p className="font-bold text-emerald-800 dark:text-emerald-200 text-sm">
                  {submittedCount} topic{submittedCount !== 1 ? "s" : ""} submitted
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                  out of {trainingTopics.length} total
                </p>
              </div>
            </div>
          )}
        </aside>

        <div className="flex-1 flex flex-col gap-6 min-w-0">
          {!selectedSectorId ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-8 md:p-12 text-center">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4">
                <MdDomain className="text-4xl text-slate-400" />
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium">Select a sector to evaluate training topics (completed / not completed).</p>
            </div>
          ) : (
            <>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 sm:p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[#0e76a8] font-bold text-sm uppercase tracking-widest">
                    <MdSchool className="text-lg" />
                    Sector training evaluation
                  </div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                    {selectedSector?.name}
                  </h1>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    Each topic can be submitted independently. Mark as completed or not completed and submit.
                  </p>
                </div>
              </div>

              {/* Loading / Error / Empty */}
              {topicsLoading && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-12 text-center">
                  <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-[#0e76a8] mb-4" />
                  <p className="text-slate-500 dark:text-slate-400 text-sm">Loading training topics…</p>
                </div>
              )}
              {topicsError && !topicsLoading && (
                <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-5">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">{topicsError}</p>
                </div>
              )}
              {!topicsLoading && !topicsError && trainingTopics.length === 0 && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-12 text-center">
                  <MdSchool className="mx-auto text-4xl text-slate-300 dark:text-slate-600 mb-3" />
                  <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                    No training topics configured for this sector.
                  </p>
                </div>
              )}

              {/* Topic cards */}
              {!topicsLoading && !topicsError && trainingTopics.length > 0 && (
                <div className="space-y-4">
                  {trainingTopics.map((topic) => (
                    <TopicCard
                      key={topic.id}
                      topic={topic}
                      sectorId={selectedSectorId}
                      sectorName={selectedSector?.name}
                      supervisorId={CURRENT_SUPERVISOR_ID}
                      onSubmitted={handleTopicSubmitted}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
